import { boundNumberToRange } from "@/pages/Tagging/utils/boundNumberToRange";

import { OUTS_PER_INNING } from "../constants/game";
import {
  GameFullResponse,
  GamePlayerRef,
  ResultingState,
} from "../types/gameFull";
import {
  BaserunnerFlags,
  buildBaseRunners,
  toBaserunnerFlags,
} from "./pitchTagging";
import { getActivitiesForPA, getLastTaggedPitch } from "./plateAppearanceFeed";

/** Values for the Post At-Bat Game State editor (spec 3.5). */
export type PlateAppearanceEditValues = {
  inning: number;
  isTopOfInning: boolean;
  hitter: GamePlayerRef;
  pitcher: GamePlayerRef;
  outsRecorded: number;
  runsScored: number;
  baseRunners: BaserunnerFlags;
};

/**
 * Loads the editor from the PA (inning, hitter, pitcher) and the post-pitch
 * tags of its last pitch (outs recorded, runs scored, base runners).
 */
export const getPlateAppearanceEditValues = (
  data: GameFullResponse,
  plateAppearanceId: string,
): PlateAppearanceEditValues | null => {
  const pa = data.plateAppearances.find(
    (p) => p.plateAppearanceId === plateAppearanceId,
  );
  if (!pa) return null;

  const lastState = getActivitiesForPA(data, plateAppearanceId).at(
    -1,
  )?.resultingState;

  return {
    inning: pa.inning,
    isTopOfInning: pa.isTopOfInning,
    hitter: pa.hitter,
    pitcher: pa.pitcher,
    outsRecorded: lastState?.outsRecorded ?? 0,
    runsScored: lastState?.runsScored ?? 0,
    baseRunners: toBaserunnerFlags(lastState?.baseRunners ?? {}),
  };
};

/** Applies the edited outs/runs/runners to the last pitch's post-pitch state. */
const applyResults = (
  state: ResultingState,
  values: PlateAppearanceEditValues,
): ResultingState => {
  // Shift the running totals so the history row reflects the edit
  const outsDelta = values.outsRecorded - state.outsRecorded;
  const runsDelta = values.runsScored - state.runsScored;
  const battingTeam = values.isTopOfInning ? "away" : "home";

  return {
    ...state,
    outsRecorded: values.outsRecorded,
    runsScored: values.runsScored,
    outs: boundNumberToRange(0, state.outs + outsDelta, OUTS_PER_INNING),
    score: {
      ...state.score,
      [battingTeam]: Math.max(0, state.score[battingTeam] + runsDelta),
    },
    baseRunners: buildBaseRunners(values.baseRunners, state.baseRunners),
  };
};

/**
 * Saves the editor (spec 3.5): outs recorded, runs scored and base runners on
 * the LAST tagged pitch (the one omnibus is re-sent); hitter, pitcher and
 * inning on the PA and ALL its pitches.
 * The live game state and other PAs are left as-is, so this can produce
 * inconsistent data, which the spec accepts for trained SPO users.
 */
export const applyPlateAppearanceEdit = (
  data: GameFullResponse,
  plateAppearanceId: string,
  values: PlateAppearanceEditValues,
): GameFullResponse => {
  const pitches = getActivitiesForPA(data, plateAppearanceId);
  const pitchIds = new Set(pitches.map((a) => a.activityId));
  const lastPitchId = getLastTaggedPitch(data, plateAppearanceId)?.activityId;

  const activities = data.activities.map((activity) => {
    if (!pitchIds.has(activity.activityId) || !activity.resultingState) {
      return activity;
    }

    const resultingState: ResultingState = {
      ...activity.resultingState,
      inning: values.inning,
      isTopOfInning: values.isTopOfInning,
      currentHitter: values.hitter,
      currentPitcher: values.pitcher,
    };

    if (activity.activityId !== lastPitchId) {
      return { ...activity, resultingState };
    }
    return {
      ...activity,
      // The runners a re-tag reports (postTagOccupiedBases)
      result: activity.result && {
        ...activity.result,
        occupiedBases: values.baseRunners,
      },
      resultingState: applyResults(resultingState, values),
    };
  });

  const runsScored = activities
    .filter((a) => pitchIds.has(a.activityId))
    .reduce((sum, a) => sum + (a.resultingState?.runsScored ?? 0), 0);

  return {
    ...data,
    activities,
    plateAppearances: data.plateAppearances.map((pa) =>
      pa.plateAppearanceId === plateAppearanceId
        ? {
            ...pa,
            inning: values.inning,
            isTopOfInning: values.isTopOfInning,
            hitter: values.hitter,
            pitcher: values.pitcher,
            runsScored,
          }
        : pa,
    ),
  };
};

export type PlateAppearancePlayers = {
  hitter?: GamePlayerRef;
  pitcher?: GamePlayerRef;
};

/**
 * Applies a hitter/pitcher change (Change Hitter, Sub Out) to a PA and every
 * one of its pitches, mirroring the omnibus re-tag of those pitches.
 */
export const applyPlateAppearancePlayers = (
  data: GameFullResponse,
  plateAppearanceId: string,
  players: PlateAppearancePlayers,
): GameFullResponse => {
  const pitchIds = new Set(
    getActivitiesForPA(data, plateAppearanceId).map((a) => a.activityId),
  );
  const { hitter, pitcher } = players;

  return {
    ...data,
    activities: data.activities.map((activity) =>
      pitchIds.has(activity.activityId) && activity.resultingState
        ? {
            ...activity,
            resultingState: {
              ...activity.resultingState,
              currentHitter: hitter ?? activity.resultingState.currentHitter,
              currentPitcher: pitcher ?? activity.resultingState.currentPitcher,
            },
          }
        : activity,
    ),
    plateAppearances: data.plateAppearances.map((pa) =>
      pa.plateAppearanceId === plateAppearanceId
        ? {
            ...pa,
            hitter: hitter ?? pa.hitter,
            pitcher: pitcher ?? pa.pitcher,
          }
        : pa,
    ),
  };
};

export type InningMove = { inning: number; isTopOfInning: boolean };

/**
 * Frontend only: shows PAs moved to another inning in the at-bat editor. The
 * game service has no way to move one yet (a direct endpoint is planned), so
 * the moves are re-applied over its updates until then.
 */
export const applyInningMoves = (
  data: GameFullResponse,
  moves: Record<string, InningMove>,
): GameFullResponse => {
  if (Object.keys(moves).length === 0) return data;
  const moveOf = (plateAppearanceId: string) =>
    moves[plateAppearanceId] as InningMove | undefined;

  return {
    ...data,
    activities: data.activities.map((activity) => {
      const move = moveOf(activity.plateAppearanceId);
      return move && activity.resultingState
        ? {
            ...activity,
            resultingState: { ...activity.resultingState, ...move },
          }
        : activity;
    }),
    plateAppearances: data.plateAppearances.map((pa) => {
      const move = moveOf(pa.plateAppearanceId);
      return move ? { ...pa, ...move } : pa;
    }),
  };
};
